import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { useQuery } from '@apollo/client';
import { useFormikContext } from 'formik-2';
import { partnerShape } from '../types';
import { TENCENT_PARTNER_NAME } from '../../consts';
import css from './PartnerConfigurationShelf.css';
import partnerAdAccountCss from './PartnerAdAccount.css';
import { Spinner, AutoCompleteField } from '../../../components/widgets';
import { GET_PARTNER_ACCOUNTS } from '../../queries';

export const PARTNER_ACCOUNT_DROPDOWN_TEST_ID = 'partner-account-dropdown';

const DEFAULT_PARTNER_CONFIG = {
    dropdownHeader: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_AD_ACCOUNT.DROPDOWN_HEADER_DEFAULT',
    extraDisplayFields: [],
};

const PARTNER_CONFIGS = {
    [TENCENT_PARTNER_NAME]: {
        dropdownHeader: 'STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_AD_ACCOUNT.DROPDOWN_HEADER_TENCENT',
        extraDisplayFields: ['qqNumber'],
    },
};

const getPartnerConfig = partnerName => ({
    ...DEFAULT_PARTNER_CONFIG,
    ...PARTNER_CONFIGS[partnerName],
});

const formatDropdownLabel = (account, partnerConfig) => {
    const loginData = account.loginData?.[0] || {};
    const extraValues = partnerConfig.extraDisplayFields.map(field => loginData[field]).filter(Boolean);

    if (extraValues.length === 0) {
        return account.loginId;
    }

    return [account.loginId, ...extraValues].join(' - ');
};

function PartnerAdAccount({ partner, isReadonly = false }) {
    const { values, setFieldValue, setFieldTouched, errors, touched } = useFormikContext();
    const { data, loading } = useQuery(GET_PARTNER_ACCOUNTS, {
        variables: { partnerId: partner.id },
    });
    const accounts = useMemo(() => data?.partnerAccounts ?? [], [data?.partnerAccounts]);
    const partnerConfig = useMemo(() => getPartnerConfig(partner.name), [partner.name]);

    // Clear stale account if it doesn't exist in available accounts
    useEffect(() => {
        const currentAccount = values.accounts?.[0];
        if (!loading && currentAccount) {
            const accountExists = accounts.some(acc => acc.loginId === currentAccount.loginId);
            if (!accountExists) {
                setFieldValue('accounts', []);
            }
        }
    }, [loading, accounts, values.accounts, setFieldValue]);

    if (loading) {
        return <Spinner show expanded />;
    }

    const currentAccount = values.accounts?.[0] || null;
    const accountExists = currentAccount && accounts.some(acc => acc.loginId === currentAccount.loginId);
    const selectedAccount = accountExists
        ? [{ value: currentAccount.loginId, label: formatDropdownLabel(currentAccount, partnerConfig) }]
        : [];

    const accountDropdownOptions = accounts.map(account => ({
        value: account.loginId,
        label: formatDropdownLabel(account, partnerConfig),
    }));

    const groupByHeader = () => ({
        [partnerConfig.dropdownHeader]: accountDropdownOptions,
    });

    const handleAccountChange = option => {
        setFieldTouched('accounts', true);

        if (!option) {
            setFieldValue('accounts', []);
            return;
        }

        const account = accounts.find(acc => acc.loginId === option.value);
        if (account) {
            setFieldValue('accounts', [account]);
        }
    };

    const errorMessage = touched.accounts && errors.accounts;

    return (
        <div className={css.section}>
            <div className={css.title}>
                <Translate id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_AD_ACCOUNT.TITLE" />
            </div>
            <div className={css.subtitle}>
                <Translate
                    id="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_AD_ACCOUNT.SUBTITLE"
                    data={{ partnerName: partner.displayName }}
                />
            </div>
            <AutoCompleteField
                dataTestId={PARTNER_ACCOUNT_DROPDOWN_TEST_ID}
                options={accountDropdownOptions}
                label="STATIC.PAGES.PARTNER_CONFIGURATION.SHELF.PARTNER_AD_ACCOUNT.TITLE"
                value={selectedAccount}
                onChange={handleAccountChange}
                containerClass={partnerAdAccountCss.dropdownContainer}
                disabled={isReadonly}
                isMulti={false}
                error={errorMessage}
                clearable
                advanced
                groupBy={groupByHeader}
            />
        </div>
    );
}

PartnerAdAccount.propTypes = {
    partner: partnerShape.isRequired,
    isReadonly: PropTypes.bool,
};

export default PartnerAdAccount;
